import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, Stethoscope, CheckCircle2, Users, Sparkles, AlertCircle, Brain } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COMPLEXITY_META = {
  low: { label: 'Простой случай', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  medium: { label: 'Средняя сложность', color: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  high: { label: 'Высокая сложность', color: 'bg-rose-500/20 text-rose-300 border-rose-500/40' },
};

export default function ConsiliumModal({ open, onClose, sessionId, initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery);
  const [isRunning, setIsRunning] = useState(false);
  const [triage, setTriage] = useState(null);
  const [specialists, setSpecialists] = useState([]);
  const [moderatorReport, setModeratorReport] = useState('');
  const [ragDone, setRagDone] = useState(false);
  const [error, setError] = useState('');
  const abortRef = useRef(null);
  const reportBottomRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
      setTriage(null);
      setSpecialists([]);
      setModeratorReport('');
      setRagDone(false);
      setError('');
    }
  }, [open, initialQuery]);

  useEffect(() => {
    reportBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [moderatorReport]);

  const runConsilium = async () => {
    if (!query.trim()) {
      toast({ title: 'Введите вопрос', variant: 'destructive' });
      return;
    }

    setIsRunning(true);
    setTriage(null);
    setSpecialists([]);
    setModeratorReport('');
    setRagDone(false);
    setError('');

    const token = localStorage.getItem('token');
    const form = new FormData();
    form.append('query', query);
    if (sessionId) form.append('session_id', sessionId);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch(`${BACKEND_URL}/api/consilium/stream`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (!response.body) throw new Error('No stream body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const chunk of lines) {
          const dataLine = chunk.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            const event = JSON.parse(dataLine.slice(6));
            handleEvent(event);
          } catch {
            /* skip malformed */
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setError(e.message || 'Ошибка соединения');
        toast({ title: 'Ошибка консилиума', description: e.message, variant: 'destructive' });
      }
    } finally {
      setIsRunning(false);
    }
  };

  const handleEvent = (e) => {
    switch (e.type) {
      case 'triage':
        setTriage(e);
        setSpecialists(e.specialists.map(s => ({ ...s, status: 'pending', opinion: '' })));
        break;
      case 'rag_start':
        break;
      case 'rag_done':
        setRagDone(true);
        break;
      case 'specialist_start':
        setSpecialists(prev => prev.map(s => s.id === e.id ? { ...s, status: 'thinking' } : s));
        break;
      case 'specialist_done':
        setSpecialists(prev => prev.map(s =>
          s.id === e.id
            ? { ...s, status: e.success ? 'done' : 'error', opinion: e.opinion || '', error: e.error }
            : s
        ));
        break;
      case 'moderator_start':
        break;
      case 'moderator_chunk':
        setModeratorReport(prev => prev + (e.content || ''));
        break;
      case 'complete':
        setModeratorReport(prev => e.full_report || prev);
        break;
      case 'error':
        setError(e.message || 'Ошибка');
        break;
      default:
        break;
    }
  };

  const stop = () => {
    abortRef.current?.abort();
    setIsRunning(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !isRunning && onClose()}>
      <DialogContent
        className="text-white w-[95vw] max-w-4xl mx-auto rounded-[22px] max-h-[90vh] flex flex-col p-0"
        style={{
          background: 'rgba(18, 18, 22, 0.92)',
          backdropFilter: 'blur(40px) saturate(1.8)',
          WebkitBackdropFilter: 'blur(40px) saturate(1.8)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        data-testid="consilium-modal"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/[0.08]">
          <DialogTitle className="flex items-center gap-2.5 text-xl">
            <Users className="w-5 h-5 text-cyan-400" />
            Виртуальный консилиум врачей
          </DialogTitle>
          <p className="text-xs text-white/50 mt-1.5">
            8 AI-специалистов работают параллельно по вашему запросу с опорой на PubMed, OpenFDA, WHO ICD-11
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Query input */}
          <div className="space-y-2">
            <label className="text-sm text-white/70">Опишите свой медицинский вопрос или симптомы:</label>
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Например: мне 55 лет, стали болеть суставы, принимаю метформин и аторвастатин, анализы в пределах нормы..."
              className="bg-white/[0.05] border-white/[0.12] text-white placeholder:text-white/30 min-h-[90px] rounded-2xl"
              disabled={isRunning}
              data-testid="consilium-query-input"
            />
          </div>

          {/* Triage result */}
          {triage && (
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4" data-testid="consilium-triage">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-semibold text-white/80">Triage-оценка</span>
                <Badge className={`border ${COMPLEXITY_META[triage.complexity]?.color || ''}`}>
                  {COMPLEXITY_META[triage.complexity]?.label || triage.complexity}
                </Badge>
              </div>
              <p className="text-xs text-white/50">{triage.reasoning}</p>
            </div>
          )}

          {/* RAG context indicator */}
          {triage && (
            <div className="flex items-center gap-2 text-xs text-white/60" data-testid="consilium-rag-status">
              {ragDone ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  Медицинские источники подключены: PubMed, OpenFDA, WHO ICD-11
                </>
              ) : (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" />
                  Поиск в PubMed, OpenFDA, WHO ICD-11...
                </>
              )}
            </div>
          )}

          {/* Specialists grid */}
          {specialists.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="consilium-specialists">
              {specialists.map((s) => (
                <div
                  key={s.id}
                  className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3 transition-all"
                  style={{
                    borderColor: s.status === 'thinking' ? 'rgba(34, 211, 238, 0.4)' :
                                 s.status === 'done' ? 'rgba(52, 211, 153, 0.3)' :
                                 s.status === 'error' ? 'rgba(244, 63, 94, 0.3)' :
                                 'rgba(255, 255, 255, 0.08)',
                  }}
                  data-testid={`specialist-${s.id}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{s.emoji}</span>
                      <span className="text-sm font-medium text-white/90">{s.name_ru}</span>
                    </div>
                    {s.status === 'pending' && <span className="text-xs text-white/40">Ожидает...</span>}
                    {s.status === 'thinking' && <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-400" />}
                    {s.status === 'done' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                    {s.status === 'error' && <AlertCircle className="w-3.5 h-3.5 text-rose-400" />}
                  </div>
                  {s.opinion && (
                    <details className="text-xs text-white/60 mt-2">
                      <summary className="cursor-pointer text-cyan-400/80 hover:text-cyan-300">Открыть мнение</summary>
                      <pre className="mt-2 whitespace-pre-wrap font-sans text-xs leading-relaxed max-h-60 overflow-y-auto">{s.opinion}</pre>
                    </details>
                  )}
                  {s.error && <p className="text-xs text-rose-400/80 mt-1.5">{s.error}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Moderator final report */}
          {moderatorReport && (
            <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.05] p-4" data-testid="consilium-report">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-semibold text-cyan-300">Итоговый отчёт консилиума</span>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-white/85 max-h-[40vh] overflow-y-auto">
                {moderatorReport}
              </pre>
              <div ref={reportBottomRef} />
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 flex items-start gap-2 text-sm text-rose-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 px-5 py-4 border-t border-white/[0.08] bg-white/[0.02]">
          {!isRunning ? (
            <>
              <Button
                onClick={runConsilium}
                disabled={!query.trim()}
                className="flex-1 rounded-full h-11 text-white"
                style={{ background: 'rgba(10, 132, 255, 0.85)' }}
                data-testid="consilium-start-btn"
              >
                <Stethoscope className="w-4 h-4 mr-2" />
                {moderatorReport ? 'Запустить снова' : 'Собрать консилиум'}
              </Button>
              <Button
                onClick={onClose}
                variant="ghost"
                className="rounded-full h-11 text-white/60 hover:text-white"
                data-testid="consilium-close-btn"
              >
                Закрыть
              </Button>
            </>
          ) : (
            <Button
              onClick={stop}
              className="flex-1 rounded-full h-11 bg-rose-500/80 hover:bg-rose-500 text-white"
              data-testid="consilium-stop-btn"
            >
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Остановить
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
